import React, { useState } from "react";
import { Box, Button, TextField, MenuItem, IconButton } from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import Header from "../../AdminComponents/Header";
import { useDispatch } from "react-redux";
import { AddCategories } from "../../../../context/Slice/VendorManagementSlice";
import { Add, Remove } from "@mui/icons-material";
import { cleanDigitSectionValue } from "@mui/x-date-pickers/internals/hooks/useField/useField.utils";
import "../Co-ordinatorManagement/CreateCordinator.css";

const CreateCategories = ({ handleFormClose, refreshUserList }) => {
  const dispatch = useDispatch();

  const [showFieldsSection, setShowFieldsSection] = useState(false);
  const [fields, setFields] = useState([
    {
      fieldName: "TotalBudget",
      fieldLabel: "Total Budget",
      fieldType: "select",
      options: [],
    },
    {
      fieldName: "SpecialBadges",
      fieldLabel: "Special Badges",
      fieldType: "multiselect",
      options: [
        "Available for Video Meetings",
        "Most Popular",
        "Premium",
        "Prime",
        "None",
      ],
    },
  ]);

  const validationSchema = yup.object({
    CategorieName: yup.string().required("Category Name is required"),
    CategorieImage: yup.mixed().required("Image is required"),
  });

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    formik.setFieldValue("CategorieImage", file);
  };

  // Function to add a new field configuration
  const addField = () => {
    setFields([
      ...fields,
      { fieldName: "", fieldLabel: "", fieldType: "", options: [] },
    ]);
  };

  // Function to remove a field (but not TotalBudget or SpecialBadges)
  const removeField = (index) => {
    if (index >= 2) {
      // Prevent removing TotalBudget and SpecialBadges
      setFields(fields.filter((_, i) => i !== index));
    }
  };

  // Function to update field properties
  const updateField = (index, property, value) => {
    const updatedFields = fields.map((field, i) =>
      i === index ? { ...field, [property]: value } : field
    );
    setFields(updatedFields);
  };

  // Function to update options for select/multiselect fields
  const updateOptions = (index, options) => {
    const updatedFields = fields.map((field, i) =>
      i === index ? { ...field, options: options.split(",") } : field
    );
    setFields(updatedFields);
  };

  const handleCategoryChange = (e) => {
    const value = e.target.value;
    formik.setFieldValue("CategorieName", value);

    // If CategorieName is VENUE, change TotalBudget label to PerDayPropertyCharges
    if (value.toUpperCase() === "VENUE") {
      setFields((prevFields) =>
        prevFields.map((field) =>
          field.fieldName === "TotalBudget"
            ? { ...field, fieldLabel: "PerDayPropertyCharges" }
            : field
        )
      );
    } else {
      // Reset back to TotalBudget if not VENUE
      setFields((prevFields) =>
        prevFields.map((field) =>
          field.fieldName === "TotalBudget"
            ? { ...field, fieldLabel: "TotalBudget" }
            : field
        )
      );
    }
  };

  const formik = useFormik({
    initialValues: {
      CategorieName: "",
      CategorieImage: null,
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      const formData = new FormData();
      formData.append("CategorieName", values.CategorieName);

      // Append image only if selected
      if (values.CategorieImage) {
        formData.append("CategorieImage", values.CategorieImage);
      }

      // Append fields as JSON string
      formData.append("CategorieFields", JSON.stringify(fields));

      try {
        const response = await dispatch(AddCategories(formData));
        console.log("response", response.payload);
        refreshUserList();
        handleFormClose();
      } catch (error) {
        console.error("Error submitting form:", error);
      }
    },
  });

  return (
    <Box m="20px">
      <Header title="Create New Vendor" subtitle="Create a New User Category" />

      <form autoComplete="off" onSubmit={formik.handleSubmit}>
        {/* Category Name Field */}
        <Box
          className="form-container"
          display="grid"
          gap="30px"
          gridTemplateColumns="repeat(4, minmax(0, 1fr))"
        >
          <TextField
            fullWidth
            variant="filled"
            type="text"
            label="Category Name"
            name="CategorieName"
            value={formik.values.CategorieName}
            onChange={handleCategoryChange} // Updated here
            error={
              formik.touched.CategorieName &&
              Boolean(formik.errors.CategorieName)
            }
            helperText={
              formik.touched.CategorieName && formik.errors.CategorieName
            }
            InputLabelProps={{ style: { fontSize: 17 } }}
            inputProps={{ style: { fontSize: 20 } }}
            sx={{ gridColumn: "span 2" }}
            className="text-field"
          />

          {/* Image Upload Field */}
          <TextField
            fullWidth
            variant="filled"
            type="file"
            label="Upload Image"
            InputLabelProps={{ shrink: true, style: { fontSize: 17 } }}
            inputProps={{ accept: "image/*", style: { fontSize: 20 } }}
            onChange={handleImageChange}
            error={formik.touched.Image && Boolean(formik.errors.Image)}
            helperText={formik.touched.Image && formik.errors.Image}
            sx={{ gridColumn: "span 2" }}
            className="text-field"
          />
        </Box>

        {/* Add Fields for Category and Back Button */}
        <Box
          className="form-button-container"
          display="flex"
          justifyContent="center"
          mt="20px"
        >
          <Button
            variant="contained"
            onClick={() => setShowFieldsSection(true)}
            sx={{ mr: 2 }}
          >
            Add Fields for Category
          </Button>
          <Button type="button" variant="contained" onClick={handleFormClose}>
            Back
          </Button>
        </Box>

        {/* Show Dynamic Fields Section after Clicking "Add Fields for Category" */}
        {showFieldsSection && (
          <>
            {/* Dynamic Fields */}
            <Box mt="20px">
              {fields.map((field, index) => (
                <Box
                  key={index}
                  display="grid"
                  gridTemplateColumns="repeat(5, 1fr)" // Increased to 5 columns
                  gap="20px"
                  alignItems="center" // Align vertically
                  mb="20px"
                >
                  <TextField
                    label="Field Name (Key)"
                    value={field.fieldName}
                    onChange={(e) =>
                      updateField(index, "fieldName", e.target.value)
                    }
                    variant="filled"
                    sx={{ gridColumn: "span 1" }}
                    inputProps={{
                      readOnly: field.fieldName === "TotalBudget", // Still ensures read-only
                    }}
                  />

                  <TextField
                    label="Field Label (Display)"
                    value={field.fieldLabel}
                    onChange={(e) =>
                      updateField(index, "fieldLabel", e.target.value)
                    }
                    variant="filled"
                    sx={{ gridColumn: "span 1" }}
                  />

                  <TextField
                    select
                    label="Field Type"
                    value={field.fieldType}
                    onChange={(e) =>
                      updateField(index, "fieldType", e.target.value)
                    }
                    variant="filled"
                    sx={{ gridColumn: "span 1" }}
                  >
                    <MenuItem value="text">Text</MenuItem>
                    <MenuItem value="number">Number</MenuItem>
                    <MenuItem value="select">Select</MenuItem>
                    <MenuItem value="multiselect">Multiselect</MenuItem>
                  </TextField>

                  {/* Reserve space for options */}
                  {field.fieldType === "select" ||
                  field.fieldType === "multiselect" ? (
                    <TextField
                      label="Options (comma-separated)"
                      value={field.options.join(",")}
                      onChange={(e) => updateOptions(index, e.target.value)}
                      variant="filled"
                      sx={{ gridColumn: "span 1" }}
                    />
                  ) : (
                    <Box sx={{ gridColumn: "span 1" }} /> // Empty box to keep layout consistent
                  )}

                  {/* Remove Button */}
                  {index >= 2 && (
                    <IconButton
                      onClick={() => removeField(index)}
                      color="secondary"
                    >
                      <Remove />
                    </IconButton>
                  )}
                </Box>
              ))}
            </Box>

            {/* Add New Field Button */}
            <Box display="flex" justifyContent="center" mt="20px">
              <Button variant="outlined" onClick={addField} startIcon={<Add />}>
                Add New Field
              </Button>
            </Box>

            {/* Submit Button */}
            <Box display="flex" justifyContent="center" mt="20px">
              <Button type="submit" variant="contained">
                Submit
              </Button>
            </Box>
          </>
        )}
      </form>
    </Box>
  );
};

export default CreateCategories;
